import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { MoreHorizontal, FolderKanban, MapPin } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Badge } from 'components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'

export interface ProjectCardCNProps {
	name: string
	locationName: string
	locationCityProv: string
	projectType: string
	image: string
	linkUrl: string
	updatedAt?: string
	isOwner?: boolean
	ownerImage?: string
	ownerName?: string
	ownerCompany?: string
	ownerEmail?: string
	onThreeDotClick?: () => void
}

export const ProjectCardCN = ({
	name,
	locationName,
	locationCityProv,
	projectType,
	image,
	linkUrl,
	updatedAt,
	isOwner = false,
	ownerImage,
	ownerName,
	ownerCompany,
	ownerEmail,
	onThreeDotClick,
}: ProjectCardCNProps) => {
	const [isModalOpen, setIsModalOpen] = useState(false)

	const handleActionClick = (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (isOwner && onThreeDotClick) {
			onThreeDotClick()
		} else if (!isOwner) {
			setIsModalOpen(true)
		}
	}

	const formattedDate = updatedAt
		? new Date(updatedAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
		: null

	return (
		<>
			<Link
				to={linkUrl}
				className="no-underline text-inherit hover:no-underline"
			>
				<div className="bg-white rounded-2xl border border-gray-200 shadow-md overflow-hidden h-[132px] flex hover:shadow-lg hover:border-gray-300 hover:scale-105 transition-all duration-200 group">
					{/* Image area */}
					<div className="w-[106px] h-full flex-shrink-0 relative overflow-hidden">
						{image ? (
							<div
								className="w-full h-full bg-cover bg-center group-hover:scale-105 transition-transform duration-300"
								style={{ backgroundImage: `url(${image})` }}
							/>
						) : (
							<div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
								<FolderKanban className="w-8 h-8 text-[#0080fe]" />
							</div>
						)}
						{/* Type icon overlay on image */}
						{image && (
							<div className="absolute top-1.5 left-1.5 bg-white shadow-sm rounded-full p-1">
								<FolderKanban className="w-3 h-3 text-[#0080fe]" />
							</div>
						)}
					</div>

					{/* Content area */}
					<div className="flex-1 min-w-0 flex flex-col justify-between p-3">
						{/* Top: name and location info */}
						<div className="min-w-0">
							<h3 className="text-sm font-semibold text-gray-900 truncate leading-tight m-0">
								{name}
							</h3>
							<p className="text-xs text-gray-500 truncate mt-0.5 mb-0 leading-tight">
								{locationName}
							</p>
							<p className="text-xs text-gray-400 truncate mt-0.5 mb-0 leading-tight">
								{locationCityProv}
							</p>
						</div>

						{/* Bottom: badges, date, and action */}
						<div className="flex items-center gap-1.5 mt-auto">
							{projectType && (
								<Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 gap-0.5">
									{projectType}
								</Badge>
							)}
							{formattedDate && (
								<span className="text-[10px] text-gray-400">
									Updated {formattedDate}
								</span>
							)}

							{/* Action button pushed to the right */}
							<div className="ml-auto flex-shrink-0">
								{isOwner ? (
									<button
										type="button"
										title="Edit"
										className="inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-gray-100 transition-colors"
										onClick={handleActionClick}
									>
										<MoreHorizontal className="w-4 h-4 text-gray-400" />
									</button>
								) : (
									<Avatar
										className="h-6 w-6 cursor-pointer"
										onClick={handleActionClick}
									>
										<AvatarImage src={ownerImage} />
										<AvatarFallback className="text-[9px]">
											{ownerName?.charAt(0)?.toUpperCase() || '?'}
										</AvatarFallback>
									</Avatar>
								)}
							</div>
						</div>
					</div>
				</div>
			</Link>

			{/* Owner info modal */}
			<Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
				<DialogContent className="max-w-[350px] p-8 relative">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<FolderKanban className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Project Owner</DialogTitle>
								<DialogDescription>View project details</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="flex justify-center mt-2">
						<Avatar className="h-[100px] w-[100px]">
							<AvatarImage src={ownerImage} />
							<AvatarFallback className="text-2xl">
								{ownerName?.charAt(0)?.toUpperCase() || '?'}
							</AvatarFallback>
						</Avatar>
					</div>
					<h2 className="pt-4 text-xl font-medium text-center">{ownerName}</h2>
					<h2 className="text-xl font-medium text-center">{ownerCompany}</h2>
					<p className="text-sm text-center">
						<a
							href={`mailto:${ownerEmail}?subject=${encodeURIComponent(name)}`}
							className="text-blue-500 hover:underline"
						>
							{ownerEmail}
						</a>
					</p>
				</DialogContent>
			</Dialog>
		</>
	)
}

export default ProjectCardCN
